import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import gql from 'graphql-tag'

import listProducts from 'sections/product/gql/query/listProducts'
import { Select, Number, Form } from 'components/forms'
import { Modal } from 'components/Modal'
import Button from 'components/Button'
import { DataTable } from 'components/DataTable'
import { useQuery, useMutation } from 'components/graphql'
import Divider from 'components/Divider'
import { useDrag, useDrop } from 'react-dnd'
import { ReactTableDefaults } from 'react-table'
import styled, { css } from 'styled-components'

const listQuery = gql`
	query listProductStorageRecord($productId: ID!) {
	  cashAssist {
		listProductStorage(productId: $productId) {
		  id
		  sortId
		  subProductId
		  type
		  factor
		  subProduct {
				id
				name
				code
		  }
		}
  }
}
`
const deleteMutation = gql`
	mutation deleteProductStorage($id: ID!) {
	  cashAssist {
		deleteProductStorage(id: $id) {
		  id
		}
  }
}
`
const addMutation = gql`
	mutation createProductStorage($required: ProductStorageInput!) {
	  cashAssist {
		createProductStorage(required: $required) {
		  id
		  sortId
		  subProductId
		  type
		  factor
		  subProduct {
				id
				name
				code
		  }
		}
	}
}
`
const updateMutation = gql`
	mutation updateProductStorage($id: ID!, $fields: ProductStorageInput!) {
	  cashAssist {
		updateProductStorage(id: $id, fields: $fields) {
		  id
		  sortId
		  subProductId
		  type
		  factor
		  subProduct {
				id
				name
				code
		  }
		}
  }
}
`

const RowContainer = styled.div`
  &.rt-tr {
    position: relative;
    ${(p) =>
    	p.highlight && css`
        	background-color: rgba(144, 238, 144, 0.4) !important; /* light green */
    	`}
  }
`

const DragableRow = ({ children, rowData, className, onDrop, onClick }) => {
	const ref = React.useRef(null)

	const [, drag] = useDrag({
		item: { type: 'row', rowData },
		canDrag: true
	})

	const [{ isOver, canDrop }, drop] = useDrop({
		accept: 'row',
		canDrop: (item) => item?.rowData?.id !== rowData?.id, // avoid dropping onto itself
		drop: (item, monitor) => {
			if (!monitor.canDrop()) {
				return
			}
			if(onDrop) {
				onDrop({ target: rowData, dropped: item.rowData })
			}
		},
		collect: (monitor) => ({
			isOver: monitor.isOver({ shallow: true }),
			canDrop: monitor.canDrop()
		})
	})

	// Merge refs: both connectors on the same DOM node
	drag(drop(ref))

	return (
		<RowContainer
			ref={ref}
			className={`rt-tr ${className || ''}`}
			highlight={isOver && canDrop}
			onClick={onClick}
		>
		  {children}
		</RowContainer>
	)
}

const Inventory = ({ itemId, mutations, formData }) => {
	const [t] = useTranslation()
	const [editItem, setEditItem] = useState()
	const { data: storageData, loading } = useQuery(listQuery, {
		variables: {
			productId: itemId
		}
	})
	const refetchQueries = [
		{ query: listQuery, variables: { productId: itemId } }
	]
	const [sendDelete] = useMutation(deleteMutation, { refetchQueries })
	const [sendAdd] = useMutation(addMutation, { refetchQueries })
	const [sendUpdate] = useMutation(updateMutation, { refetchQueries })

	const disableDrag = formData?.properties?.storageType !== 'SUBTRACT'
	const disallowedSubProductIds = (storageData?.listProductStorage || [])
		.map(storage => storage.subProductId)
		.filter(id => id !== editItem?.subProductId)

	const handleSubmit = fields => {
		const submitedId = editItem.id
		if(!submitedId) {
			//new item
			setEditItem()
			return sendAdd({
				variables: {
					required: {
						productId: itemId,
						...fields
					}
				}
			})
		}
		// update item
		setEditItem()
		return sendUpdate({
			variables: {
				id: submitedId,
				fields: {
					productId: itemId,
					...fields
				}
			}
		})
	}

	const handleDelete = id => {
		return sendDelete({
			variables: { id }
		})
	}
	const handleChangeType = fields => mutations.updateItem(fields, { refetchQueries })

	const handleChangeOrder = ({ dropped, target }) => {
		sendUpdate({
			variables: {
				id: dropped.id,
				fields: {
					productId: itemId,
					subProductId: dropped.subProductId,
					sortId: target.sortId
				}
			}
		})
		sendUpdate({
			variables: {
				id: target.id,
				fields: {
					productId: itemId,
					subProductId: target.subProductId,
					sortId: dropped.sortId
				}
			}
		})
	}

	return(
		<>
			<Form initialValues={formData} onSubmit={handleChangeType} smartSubmit>
				<Select
					field='properties.storageType'
					label={t('product_section.fields.type')}
					options={[{
						label: t('common.strings.SUBTRACT'),
						value: 'SUBTRACT'
					}, {
						label: t('common.strings.CONVERT'),
						value: 'CONVERT'
					}, {
						label: t('common.strings.COMPONENT'),
						value: 'COMPONENT'
					}]}
				/>
			</Form>
			<Divider/>
			{formData.properties?.storageType &&
				<DataTable
					data={storageData?.listProductStorage?.map(item => ({
						...item,
						type: t(`common.strings.${item.type}`)
					})) || []}
					loading={loading}
					columns={[
						{
							accessor: 'id',
							show: false
						},
						{
							Header: t('product_section.fields.number'),
							accessor: 'subProduct.code'
						}, {
							Header: t('product_section.fields.targetPlu'),
							accessor: 'subProduct.name'
						}, {
							Header: t('product_section.fields.type'),
							accessor: 'type'
						}, {
							Header: t('product_section.fields.factor'),
							accessor: 'factor'
						}
					]}
					selectRow={{
						selected: -1
					}}
					width={800}
					onRowClick={(state, { original }) => setEditItem(original)}
					onAddRecord={() => setEditItem({})}
					mutateRow={{
						onRowMutate: handleDelete
					}}
					sortable={false}
					TrComponent={(props) => {
						const data = { ...props?.children[0]?.props?.children?.props?.original }
						return disableDrag ?
							<ReactTableDefaults.TrComponent {...props}/> :
							<DragableRow
								{...props}
								rowData={data}
								onDrop={handleChangeOrder}
							/>
					}}
				/>
			}
			{editItem && <Modal unmount={() => setEditItem()} label={t('product_section.strings.define_connection')}>
				<Form initialValues={editItem} onSubmit={handleSubmit}>
					<Select
						isSearchable
						field='subProductId'
						label={t('product_section.fields.targetPlu')}
						valueField='id'
						labelField='name'
						remoteOptions={{
							query: listProducts,
							searchFields: ['name', 'code'],
							modifyOptions: (options => options.filter(opt =>
								opt.id !== itemId && !disallowedSubProductIds.includes(opt.id)
							).map(option => ({
								...option,
								name: `${option.code} | ${option.name}`
							})))
						}}
						validate={v => !v ? t('validators.required') : null}
					/>
					<Number
						label={t('product_section.fields.factor')}
						precision={3}
						field='factor'
						validate={v => !v ? t('validators.required') : null}
					/>
					<Button text={t('common.buttons.save')} type='submit'/>
					<Button text={t('common.buttons.abort')} type='button' status='cancelation' onClick={() => setEditItem()}/>
				</Form>
			</Modal>}
		</>
	)
}

export default Inventory
